import React, { useEffect, useRef, useState } from 'react';
import './style.css';

interface DialogProps {
  visible: boolean;
  onHide: () => void;
  header?: React.ReactNode | string;
  children: React.ReactNode;
  className?: string;
  maskClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  closable?: boolean;
  draggable?: boolean;
  style?: React.CSSProperties;
}

const Dialog: React.FC<DialogProps> = ({
  visible,
  onHide,
  header,
  children,
  className = '',
  maskClassName = '',
  contentClassName = '',
  headerClassName = '',
  closable = true,
  draggable = false,
  style = {}
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && visible && closable) {
        onHide();
      }
    };

    if (visible) {
      document.addEventListener('keydown', handleEscape);
      // Não altera o overflow do body se já estiver hidden (sidebar pode estar aberto)
      const currentOverflow = document.body.style.overflow;
      if (currentOverflow !== 'hidden') {
        document.body.style.overflow = 'hidden';
      }
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      // Só restaura o overflow se o modal está fechando e não há outros modais
      if (!visible) {
        const activeModals = document.querySelectorAll('.dialog-overlay');
        if (activeModals.length <= 1) {
          document.body.style.overflow = '';
        }
      }
    };
  }, [visible, closable, onHide]);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!draggable) return;
    
    const rect = dialogRef.current?.getBoundingClientRect();
    if (!rect) return;

    setIsDragging(true);
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || !draggable) return;

    setPosition({
      x: e.clientX - dragOffset.x,
      y: e.clientY - dragOffset.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && closable) {
      onHide();
    }
  };

  if (!visible) return null;

  const dialogStyle = draggable && (position.x !== 0 || position.y !== 0) 
    ? { 
        ...style, 
        transform: `translate(${position.x}px, ${position.y}px)`,
        position: 'absolute' as const
      }
    : style;

  return (
    <div 
      className={`dialog-overlay ${maskClassName}`} 
      onClick={handleOverlayClick}
    >
      <div 
        ref={dialogRef}
        className={`dialog-container ${className} ${draggable ? 'dialog-draggable' : ''}`}
        style={dialogStyle}
      >
        {header && (
          <div 
            className={`dialog-header ${headerClassName}`}
            onMouseDown={handleMouseDown}
          >
            <div className="dialog-title">
              {typeof header === 'string' ? <h3>{header}</h3> : header}
            </div>
            {closable && (
              <button 
                className="dialog-close-button" 
                onClick={onHide}
                type="button"
              >
                ×
              </button>
            )}
          </div>
        )}
        <div className={`dialog-content ${contentClassName}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Dialog;
